// src/pages/Sessions.jsx
// User sessions: sign-ins and sign-outs (3.9.1, every role with "User
// sessions" in Roles & Access). The server decides whose: Super Admins
// everyone; others themselves and the people below them in their account
// (Admins also other Admins), never above. Shown in Users → User sessions,
// or as "My Sessions" next to Incoming Faxes for people without the Users
// tab (ownOnly: just their own, so no person filters).
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import ResetFiltersButton, { filtersDiffer } from '../components/ResetFiltersButton';
import EmptyState from '../components/EmptyState';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import MultiSearchSelect from '../components/MultiSearchSelect';
import SortTh, { useSort } from '../components/SortTh';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import TeamFilter, { useClientTeams, teamText } from '../components/TeamFilter';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

const REASON_LABELS = {
  sign_out      : { label: 'Sign Out',         bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  inactivity    : { label: 'Inactivity',        bg: 'var(--c-fff8e1)', color: 'var(--t-e65100)' },
  browser_unload: { label: 'Refresh/Close',     bg: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' },
  timed_out     : { label: 'Timed Out',         bg: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)' },
};

// My Sessions column widths: Name, Login Method, Login Time, Logout Time,
// Duration, Reason, IP Address (3.30.4)
const OWN_WIDTHS = ['15%', '15%', '19%', '19%', '9%', '12%', '11%'];

const SESSION_DEFAULTS = { startDate: '', endDate: '', email: [], role: [], account: '', limit: '50', team: '' };   // Show: newest 50 (3.18.3)

export default function Sessions({ ownOnly = false, inDialog = false }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, effectiveInfo, viewAs } = useAuth();
  // Account picker (3.18.1): Super Admins on All accounts can narrow the
  // list to one account's people without switching the portal to it
  const info = effectiveInfo || {};
  const allAccountsView = !ownOnly && info.level === 'super_admin' && (!info.accountId || info.accountId === 'all') && !viewAs?.readOnly;
  const { notify }            = useNotify();
  const [data, setData]       = useState([]);
  // Column sorting (3.16.0)
  const { sorted: sortedData, sort, onSort } = useSort(data, {
    email: s => s.email, name: s => s.name, role: s => s.role || '', team: teamText, method: s => s.loginMethod,
    login: s => s.loginTime || '', logout: s => s.logoutTime || '', duration: s => (s.loginTime && s.logoutTime ? Date.parse(s.logoutTime) - Date.parse(s.loginTime) : ''),
    reason: s => s.reason, ip: s => s.ip,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState(SESSION_DEFAULTS);
  const { pageRows, pager } = usePager(sortedData, filters.limit);   // page by page (3.26.0)
  const [applied, setApplied] = useState(SESSION_DEFAULTS); // filters of the last search
  // Every role there is, from the server (3.18.2), for the Role filter
  const [roleOptions, setRoleOptions] = useState([]);
  const clientTeams = useClientTeams();   // 3.19.5
  const [total, setTotal] = useState(0);   // sessions matching, before Show

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); }, []);

  async function loadSessions(f = filters) {
    setLoading(true);
    setError('');
    try {
      const token  = await getToken();
      const params = new URLSearchParams();
      if (f.startDate)  params.append('startDate',  f.startDate);
      if (f.endDate)    params.append('endDate',    f.endDate);
      if (f.email.length)      params.append('email',      f.email.join(','));
      if (f.role.length)       params.append('role',       f.role.join(','));
      if (f.account)           params.append('account',    f.account);
      if (f.team)              params.append('team',       f.team);   // 3.19.5
      params.append('limit', 'all');   // every row; shown page by page (3.26.0)
      if (ownOnly) params.append('mine', '1');   // My Sessions: just their own (3.29.1)

      const res  = await fetch(`${API_BASE}/portal/sessions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load sessions');
      setData(json.data || []);
      setTotal(json.total ?? (json.data || []).length);
      setRoleOptions(json.roles || []);
      setApplied(f);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function resetFilters() {
    setFilters(SESSION_DEFAULTS);
    loadSessions(SESSION_DEFAULTS);
  }

  async function handleDownload() {
    try {
      const token  = await getToken();
      const params = new URLSearchParams();
      if (filters.startDate)  params.append('startDate',  filters.startDate);
      if (filters.endDate)    params.append('endDate',    filters.endDate);
      if (filters.email.length)      params.append('email',      filters.email.join(','));
      if (filters.role.length)       params.append('role',       filters.role.join(','));
      if (filters.account)           params.append('account',    filters.account);
      if (filters.team)              params.append('team',       filters.team);
      if (ownOnly)                   params.append('mine',       '1');   // My Sessions (3.29.1)

      const res  = await fetch(`${API_BASE}/portal/sessions/download?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Session_Report_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      notify({ tone: 'error', title: 'Download failed', detail: e.message });
    }
  }

  function handleFilterChange(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  // Summary stats
  const totalSessions  = data.length;
  const uniqueUsers    = new Set(data.map(d => d.email)).size;
  const roles          = [...new Set(data.map(d => d.role).filter(Boolean))].sort();
  const showTeam       = !ownOnly && (clientTeams.length > 0 || data.some(d => (d.teamNames || []).length));


  return (
    <div>
      <div style={S.header}>
        {inDialog ? <div /> : (
          <div>
            <h2 style={S.pageTitle} className="cp-title"><Emo e="🔐" /> Sessions</h2>
            <p style={S.pageSubtitle}>{ownOnly ? 'When you signed in and out of the portal' : 'When people signed in and out: you, and the people below you at your company'}</p>
          </div>
        )}
        <button onClick={handleDownload} style={S.downloadBtn}><Emo e="⬇" /> Download (.xlsx)</button>
      </div>

      {/* Summary Cards */}
      {data.length > 0 && (
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardValue}>{totalSessions}</div>
            <div style={S.cardLabel}>Total Sessions</div>
          </div>
          {/* My Sessions (3.30.4): always one person and one role, so not shown */}
          {!ownOnly && <div style={S.card}>
            <div style={S.cardValue}>{uniqueUsers}</div>
            <div style={S.cardLabel}>Unique Users</div>
          </div>}
          {!ownOnly && <div style={S.card}>
            <div style={S.cardValue}>{roles.length}</div>
            <div style={S.cardLabel}>Roles</div>
          </div>}
          <div style={S.card}>
            <div style={S.cardValue}>{data.filter(d => d.reason === 'sign_out').length}</div>
            <div style={S.cardLabel}>Clean Sign Outs</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate}
              onChange={e => handleFilterChange('startDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate}
              onChange={e => handleFilterChange('endDate', e.target.value)} style={S.filterInput} />
          </div>
          {allAccountsView && (
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>{clientWord}</label>
              <select value={filters.account} onChange={e => { const f = { ...filters, account: e.target.value }; setFilters(f); loadSessions(f); }} style={S.filterInput}>
                <option value="">All clients</option>
                {(info.accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}
          {!ownOnly && <>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Email</label>
            <MultiSearchSelect suggestType="sessions" field="email"
              values={filters.email} onChange={v => handleFilterChange('email', v)}
              placeholder="user@domain.com" />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Role</label>
            <MultiSearchSelect options={roleOptions}
              values={filters.role} onChange={v => handleFilterChange('role', v)}
              placeholder="e.g. Admin" />
          </div>
          </>}
          {!ownOnly && <TeamFilter teams={clientTeams} value={filters.team} onChange={v => handleFilterChange('team', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />}
          <ShowSelect value={filters.limit} onChange={v => { const f = { ...filters, limit: v }; setFilters(f); loadSessions(f); }}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={{ ...S.filterGroup, justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => loadSessions()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
              <ResetFiltersButton dirty={filtersDiffer(filters, SESSION_DEFAULTS)}
                onReset={resetFilters} />
            </div>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div style={S.resultCount}>
        {loading ? 'Loading...' : total > data.length ? `Newest ${data.length} of ${total} sessions` : `${data.length} session${data.length !== 1 ? 's' : ''} found`}
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          {/* My Sessions (3.30.4): each column as wide as what it holds */}
          {ownOnly && <colgroup>{OWN_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>}
          <thead>
            <tr style={S.thead}>
              {/* My Sessions (3.30.4): just their name; no Email or Role */}
              {!ownOnly && <SortTh k="email" label="Email" sort={sort} onSort={onSort} style={S.th} />}
              <SortTh k="name" label="Name" sort={sort} onSort={onSort} style={S.th} />
              {!ownOnly && <SortTh k="role" label="Role" sort={sort} onSort={onSort} style={S.th} />}
              {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={onSort} style={S.th} />}
              <SortTh k="method" label="Login Method" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="login" label="Login Time" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="logout" label="Logout Time" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="duration" label="Duration" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="reason" label="Reason" sort={sort} onSort={onSort} style={S.th} />
              <SortTh k="ip" label="IP Address" sort={sort} onSort={onSort} style={S.th} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={S.emptyCell}><LoadingSpinner /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={10} style={S.emptyCell}>
                <EmptyState icon="🔐" filtered={filtersDiffer(applied, SESSION_DEFAULTS)} onClear={resetFilters}
                  filteredText="No sessions match these filters."
                  emptyText="No sign-in sessions recorded yet." />
              </td></tr>
            ) : pageRows.map((s, i) => {
              const reasonStyle = REASON_LABELS[s.reason] || { label: s.reason || '—', bg: 'var(--c-f5f5f5)', color: 'var(--t-666666)' };
              return (
                <tr key={s.key || s.sessionId} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                  {!ownOnly && <td style={{ ...S.td, color: 'var(--t-00695c)', fontWeight: 500 }}>{s.email}</td>}
                  <td style={ownOnly ? { ...S.td, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : S.td} title={ownOnly ? s.name || '' : undefined}>{s.name || info.name || '—'}</td>
                  {!ownOnly && <td style={S.td}>
                    <span style={{ background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {s.role || '—'}
                    </span>
                  </td>}
                  {showTeam && <td style={S.td}>{teamText(s) || '—'}</td>}
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{s.loginMethod || '—'}</td>
                  <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{formatDate(s.loginTime)}</td>
                  <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{s.logoutTime ? formatDate(s.logoutTime) : '—'}</td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{s.duration || '—'}</td>
                  <td style={S.td}>
                    <span style={{ background: reasonStyle.bg, color: reasonStyle.color, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {reasonStyle.label}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12, color: 'var(--t-78909c)', whiteSpace: 'nowrap' }}>{s.ip || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pager} label="sessions" />}
    </div>
  );
}

// 3.40.1: the portal's shared loading look (components/Loader)
function LoadingSpinner() {
  return <Loader label="Loading sessions…" />;
}

const S = {
  header      : { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle   : { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 22, fontWeight: 700 },
  pageSubtitle: { margin: 0, color: 'var(--t-78909c)', fontSize: 14 },
  downloadBtn : { padding: '9px 18px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  cards       : { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 24 },
  card        : { background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--c-e0f2f1)', textAlign: 'center' },
  cardValue   : { fontSize: 26, fontWeight: 700, color: 'var(--t-00695c)', marginBottom: 4 },
  cardLabel   : { fontSize: 12, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterBar   : { background: 'var(--c-ffffff)', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', border: '1px solid var(--b-e8f5e9)' },
  filterRow   : { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' },
  filterGroup : { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 },
  filterLabel : { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterInput : { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit' },
  searchBtn   : { padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  resultCount : { fontSize: 14, color: 'var(--t-546e7a)', marginBottom: 12 },
  errorBanner : { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap   : { background: 'var(--c-ffffff)', borderRadius: 12, overflowX: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table       : { width: '100%', borderCollapse: 'collapse' },
  thead       : { background: 'var(--c-00695c)' },
  th          : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr          : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td          : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  emptyCell   : { textAlign: 'center', padding: '40px 0' },
};